import { useState, useEffect, useCallback } from 'react';
import { fetchWorkspacesByClient, createWorkspace } from '../../services/CadernoSelectorRepository';

/**
 * CadernoSelector — Componente reutilizável para selecionar Caderno de Autos.
 * Busca os cadernos vinculados ao clientId na coleção autos_workspaces.
 *
 * Props:
 *   clientId  — ID do cliente (obrigatório para buscar cadernos)
 *   value     — cadernoId selecionado atualmente
 *   onChange  — callback(cadernoId, cadernoTitle) chamado ao selecionar
 *   disabled  — desabilita o select
 *   label     — texto do label (default: "Vincular a Processo/Caderno")
 */
const CadernoSelector = ({ clientId, clientName = '', value = '', onChange, disabled = false, label = 'Vincular a Processo/Caderno' }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const loadWorkspaces = useCallback(async () => {
        const cid = String(clientId || '').trim();
        if (!cid) {
            setWorkspaces([]);
            return;
        }

        setLoading(true);
        try {
            const out = await fetchWorkspacesByClient(cid, 80);
            setWorkspaces(out);
        } catch {
            setWorkspaces([]);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        loadWorkspaces();
    }, [loadWorkspaces]);

    const handleCreateCaderno = useCallback(async () => {
        const cid = String(clientId || '').trim();
        if (!cid) return;
        setIsCreating(true);
        try {
            const { id, alias } = await createWorkspace(cid, clientName);
            await loadWorkspaces();
            onChange?.(id, alias);
        } catch (err) {
            console.error('[CadernoSelector] Erro ao criar caderno:', err);
        } finally {
            setIsCreating(false);
        }
    }, [clientId, clientName, loadWorkspaces, onChange]);

    // Auto-selecionar se houver exatamente 1 caderno e nenhum valor selecionado
    useEffect(() => {
        if (!value && workspaces.length === 1) {
            const w = workspaces[0];
            const wid = String(w?.id || '').trim();
            const title = String(w?.alias || w?.name || w?.pieceType || w?.title || 'Processo').trim();
            if (wid) onChange?.(wid, title);
        }
    }, [workspaces, value, onChange]);

    const handleChange = (e) => {
        const selectedId = String(e.target.value || '').trim();
        if (!selectedId) {
            onChange?.('', '');
            return;
        }
        const ws = workspaces.find((w) => String(w?.id || '') === selectedId);
        const title = String(ws?.alias || ws?.name || ws?.pieceType || ws?.title || 'Processo').trim();
        onChange?.(selectedId, title);
    };

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">{label}</div>
            <select
                value={value}
                onChange={handleChange}
                disabled={disabled || loading}
                className={`w-full px-3 py-2 rounded-lg border bg-white text-[11px] font-extrabold uppercase tracking-widest hover:bg-slate-50 transition-colors ${!value ? 'border-red-300 text-red-500' : 'border-slate-200 text-slate-700'}`}
                title="Selecione o Caderno de Autos ao qual este documento será vinculado"
            >
                <option value="" disabled>{loading ? 'Carregando cadernos...' : '— Selecione um Caderno (obrigatório) —'}</option>
                {workspaces.map((w) => {
                    const wid = String(w?.id || '').trim();
                    if (!wid) return null;
                    const displayName = String(w?.alias || w?.name || w?.pieceType || w?.title || 'Processo').trim();
                    const processNumber = String(w?.processNumber || '').trim();
                    const displayLabel = processNumber ? `${displayName} — ${processNumber}` : displayName;
                    return (
                        <option key={wid} value={wid}>
                            {displayLabel}
                        </option>
                    );
                })}
            </select>
            {!loading && !isCreating && String(clientId || '').trim() ? (
                <div className="mt-2 flex items-center gap-2">
                    {workspaces.length === 0 ? (
                        <span className="flex-1 text-[10px] text-red-400 font-semibold">Nenhum Caderno encontrado — crie um para vincular.</span>
                    ) : (
                        <span className="flex-1 text-[10px] text-slate-400">Ou crie um novo caderno:</span>
                    )}
                    <button
                        type="button"
                        onClick={handleCreateCaderno}
                        disabled={disabled}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#C5A059] hover:bg-[#a8844a] disabled:opacity-50 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0"
                    >
                        <i className="fas fa-plus text-[9px]" /> Criar Caderno
                    </button>
                </div>
            ) : isCreating ? (
                <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-400">
                    <i className="fas fa-spinner fa-spin text-[9px]" /> Criando caderno...
                </div>
            ) : null}
        </div>
    );
};

export { CadernoSelector };
export default CadernoSelector;
